import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { ArrowBack, CheckCircle, Cancel, Calculate } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBookingById } from '../../store/slices/bookingSlice';
import bookingService from '../../services/api/bookingService';
import notificationService from '../../services/api/notificationService';
import { BookingStatusChip } from '../../utils/bookingStatus';
import { resolveBookingPackageLabel } from '../../utils/bookingPackageLabel';

const BookingDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentBooking, loading } = useSelector((state) => state.bookings);
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = React.useState(false);
  const [cancelReason, setCancelReason] = React.useState('');
  const [adminNotes, setAdminNotes] = React.useState('');
  const [actionLoading, setActionLoading] = React.useState(false);
  const [actionError, setActionError] = React.useState('');

  useEffect(() => {
    if (id) {
      dispatch(fetchBookingById(id));
    }
  }, [id, dispatch]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const sendConfirmationEmailToCustomer = async (booking) => {
    const email =
      booking.guestEmail ||
      booking.customizedData?.email ||
      booking.userEmail;
    if (!email) return;

    const guestName =
      [booking.guestFirstName, booking.guestLastName].filter(Boolean).join(' ') ||
      booking.customizedData?.yourName ||
      'Guest';

    await notificationService.sendBookingReceiptEmail({
      bookingId: booking.id,
      bookingReference: booking.bookingReference,
      recipientEmail: email,
      guestName,
      packageName: resolveBookingPackageLabel(booking),
      travelStartDate: booking.travelStartDate,
      travelEndDate: booking.travelEndDate,
      numberOfTravelers: booking.numberOfTravelers,
      totalAmount: booking.totalAmount,
      bookingPin: booking.bookingPin || 'See confirmation email',
      includeLoginDetails: false,
      guestEmail: email,
      guestPassword: '',
    });
  };

  const handleConfirm = async () => {
    setActionError('');
    setActionLoading(true);
    try {
      await bookingService.confirmBooking(id, adminNotes);
      try {
        await sendConfirmationEmailToCustomer(currentBooking);
      } catch (notifyErr) {
        console.warn('Booking confirmed but customer email failed:', notifyErr);
      }
      setConfirmDialogOpen(false);
      setAdminNotes('');
      dispatch(fetchBookingById(id));
    } catch (error) {
      console.error('Error confirming booking:', error);
      const msg = error?.response?.data?.message || error.message || 'Failed to confirm booking';
      setActionError(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      await bookingService.cancelBooking(id, cancelReason);
      try {
        await sendConfirmationEmailToCustomer({
          ...currentBooking,
          status: 'Cancelled',
        });
      } catch (notifyErr) {
        console.warn('Cancel saved but customer email failed:', notifyErr);
      }
      setCancelDialogOpen(false);
      setCancelReason('');
      dispatch(fetchBookingById(id));
    } catch (error) {
      console.error('Error cancelling booking:', error);
      const msg = error?.response?.data?.message || error.message || 'Failed to cancel booking';
      setActionError(msg);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!currentBooking) {
    return (
      <Container>
        <Alert severity="error">Booking not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/bookings')}
        sx={{ mb: 2 }}
      >
        Back to Bookings
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Booking Details
        </Typography>
        {currentBooking.status?.toLowerCase() === 'pending' && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              onClick={() => setConfirmDialogOpen(true)}
            >
              Confirm Booking
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Cancel />}
              onClick={() => setCancelDialogOpen(true)}
            >
              Cancel Booking
            </Button>
          </Box>
        )}
      </Box>

      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Booking Information
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Reference Number
                </Typography>
                <Typography variant="h6" color="primary" gutterBottom>
                  {currentBooking.bookingReference}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <BookingStatusChip status={currentBooking.status} sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Booking Date
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatDate(currentBooking.bookingDate)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Package
                </Typography>
                <Typography variant="body1">
                  {resolveBookingPackageLabel(currentBooking)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Travel Information
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Travel Dates
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatDate(currentBooking.travelStartDate)} - {formatDate(currentBooking.travelEndDate)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Number of Travelers
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {currentBooking.numberOfTravelers}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Total Amount
                </Typography>
                <Typography variant="h5" color="primary">
                  {currentBooking.currency === 'PKR' 
                    ? `PKR ${Number(currentBooking.totalAmount || 0).toLocaleString()}` 
                    : `$${Number(currentBooking.totalAmount || 0).toLocaleString()} ${currentBooking.currency || 'USD'}`}
                </Typography>
                {currentBooking.discountAmount > 0 && (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Discount Applied
                    </Typography>
                    <Typography variant="body1" color="success.main">
                      -${currentBooking.discountAmount}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {currentBooking.priceBreakdown && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Price Breakdown
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        {currentBooking.priceBreakdown.basePrice && (
                          <TableRow>
                            <TableCell>Base Package Price</TableCell>
                            <TableCell align="right">${currentBooking.priceBreakdown.basePrice}</TableCell>
                          </TableRow>
                        )}
                        {currentBooking.priceBreakdown.hotelCost && (
                          <TableRow>
                            <TableCell>Hotel Accommodation</TableCell>
                            <TableCell align="right">${currentBooking.priceBreakdown.hotelCost}</TableCell>
                          </TableRow>
                        )}
                        {currentBooking.priceBreakdown.transportCost && (
                          <TableRow>
                            <TableCell>Transportation</TableCell>
                            <TableCell align="right">${currentBooking.priceBreakdown.transportCost}</TableCell>
                          </TableRow>
                        )}
                        {currentBooking.priceBreakdown.ziyaratCost && (
                          <TableRow>
                            <TableCell>Ziyarat</TableCell>
                            <TableCell align="right">${currentBooking.priceBreakdown.ziyaratCost}</TableCell>
                          </TableRow>
                        )}
                        {currentBooking.priceBreakdown.seasonalSurcharge && (
                          <TableRow>
                            <TableCell>Seasonal Surcharge</TableCell>
                            <TableCell align="right">+${currentBooking.priceBreakdown.seasonalSurcharge}</TableCell>
                          </TableRow>
                        )}
                        {currentBooking.priceBreakdown.discount && (
                          <TableRow>
                            <TableCell>Discount</TableCell>
                            <TableCell align="right" sx={{ color: 'success.main' }}>-${currentBooking.priceBreakdown.discount}</TableCell>
                          </TableRow>
                        )}
                        <TableRow>
                          <TableCell><strong>Total</strong></TableCell>
                          <TableCell align="right"><strong>${currentBooking.totalAmount}</strong></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          )}

          {currentBooking.hotelDetails && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Hotel Details
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Hotel Name
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {currentBooking.hotelDetails.name}
                  </Typography>
                  {currentBooking.hotelDetails.stars && (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        Star Rating
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {'⭐'.repeat(currentBooking.hotelDetails.stars)}
                      </Typography>
                    </>
                  )}
                  {currentBooking.hotelDetails.distanceFromHaram != null && (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        Distance from Haram
                      </Typography>
                      <Typography variant="body1">
                        {currentBooking.hotelDetails.distanceFromHaram} meters
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {currentBooking.transportDetails && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Transportation
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Transport Type
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {currentBooking.transportDetails.type || 'Private Car'}
                  </Typography>
                  {currentBooking.transportDetails.cost && (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        Transport Cost
                      </Typography>
                      <Typography variant="body1">
                        ${currentBooking.transportDetails.cost}
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {currentBooking.travelerDetails && currentBooking.travelerDetails.length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Traveler Details
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Phone</TableCell>
                          <TableCell>Passport</TableCell>
                          <TableCell>Nationality</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {currentBooking.travelerDetails.map((traveler, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              {traveler.firstName} {traveler.lastName}
                              {traveler.isPrimaryTraveler && (
                                <Chip label="Primary" size="small" sx={{ ml: 1 }} />
                              )}
                            </TableCell>
                            <TableCell>{traveler.email}</TableCell>
                            <TableCell>{traveler.phone || 'N/A'}</TableCell>
                            <TableCell>{traveler.passportNumber || 'N/A'}</TableCell>
                            <TableCell>{traveler.nationality || 'N/A'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          )}

          {currentBooking.specialRequests && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Special Requests
                  </Typography>
                  <Typography variant="body1">
                    {currentBooking.specialRequests}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {currentBooking.adminNotes && (
            <Grid item xs={12}>
              <Card sx={{ backgroundColor: '#fff9e6' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="warning.main">
                    Admin Notes (Internal)
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ whiteSpace: 'pre-line', fontFamily: 'monospace' }}
                  >
                    {currentBooking.adminNotes}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Customized Booking Complete Data */}
          {currentBooking.customizedData && (
            <Grid item xs={12}>
              <Card sx={{ backgroundColor: '#e3f2fd', border: '2px solid #2196f3' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Calculate />
                    Customized Package Details (Complete Customer Data)
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  
                  <Grid container spacing={2}>
                    {/* Basic Information */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
                        📋 Basic Information
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">Departure Date</Typography>
                      <Typography variant="body1" fontWeight="medium">{currentBooking.customizedData.departureDate || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">Flying From</Typography>
                      <Typography variant="body1" fontWeight="medium">{currentBooking.customizedData.flyFrom || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">Package Type</Typography>
                      <Typography variant="body1" fontWeight="medium">{currentBooking.customizedData.packageType || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">Total Passengers</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {(currentBooking.customizedData.adults || 0) + (currentBooking.customizedData.children || 0) + (currentBooking.customizedData.infants || 0)}
                      </Typography>
                    </Grid>
                    <Grid item xs={4} md={2}>
                      <Typography variant="body2" color="text.secondary">Adults</Typography>
                      <Typography variant="body1">{currentBooking.customizedData.adults || 0}</Typography>
                    </Grid>
                    <Grid item xs={4} md={2}>
                      <Typography variant="body2" color="text.secondary">Children</Typography>
                      <Typography variant="body1">{currentBooking.customizedData.children || 0}</Typography>
                    </Grid>
                    <Grid item xs={4} md={2}>
                      <Typography variant="body2" color="text.secondary">Infants</Typography>
                      <Typography variant="body1">{currentBooking.customizedData.infants || 0}</Typography>
                    </Grid>

                    {/* Stay Duration */}
                    <Grid item xs={12} sx={{ mt: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
                        🏨 Stay Duration (Configurable Entries)
                      </Typography>
                    </Grid>
                    {currentBooking.customizedData.includeFirstEntry && (
                      <>
                        <Grid item xs={12} md={4}>
                          <Card variant="outlined" sx={{ bgcolor: '#fff3e0' }}>
                            <CardContent>
                              <Typography variant="subtitle2" color="primary">First Entry</Typography>
                              <Typography variant="body2">Location: {currentBooking.customizedData.firstEntryLocation}</Typography>
                              <Typography variant="body2">Nights: {currentBooking.customizedData.firstEntryNights}</Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </>
                    )}
                    {currentBooking.customizedData.includeSecondEntry && (
                      <>
                        <Grid item xs={12} md={4}>
                          <Card variant="outlined" sx={{ bgcolor: '#e8f5e9' }}>
                            <CardContent>
                              <Typography variant="subtitle2" color="success.main">Second Entry</Typography>
                              <Typography variant="body2">Location: {currentBooking.customizedData.secondEntryLocation}</Typography>
                              <Typography variant="body2">Nights: {currentBooking.customizedData.secondEntryNights}</Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </>
                    )}
                    {currentBooking.customizedData.includeThirdEntry && (
                      <>
                        <Grid item xs={12} md={4}>
                          <Card variant="outlined" sx={{ bgcolor: '#fce4ec' }}>
                            <CardContent>
                              <Typography variant="subtitle2" color="error.main">Third Entry</Typography>
                              <Typography variant="body2">Location: {currentBooking.customizedData.thirdEntryLocation}</Typography>
                              <Typography variant="body2">Nights: {currentBooking.customizedData.thirdEntryNights}</Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </>
                    )}

                    {/* Transportation & Ziaraat */}
                    <Grid item xs={12} sx={{ mt: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
                        🚗 Transportation & Ziaraat Tours
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="text.secondary">Transport Choice</Typography>
                      <Typography variant="body1" fontWeight="medium">{currentBooking.customizedData.transportChoice || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Typography variant="body2" color="text.secondary">Makkah Ziaraat</Typography>
                      <Typography variant="body1">{currentBooking.customizedData.ziaraatMakkah || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Typography variant="body2" color="text.secondary">Madina Ziaraat</Typography>
                      <Typography variant="body1">{currentBooking.customizedData.ziaraatMadina || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="text.secondary">Online Support</Typography>
                      <Chip 
                        label={currentBooking.customizedData.onlineSupport ? 'Yes - Included' : 'Not Included'} 
                        color={currentBooking.customizedData.onlineSupport ? 'success' : 'default'}
                        size="small"
                      />
                    </Grid>

                    {/* Contact Information */}
                    <Grid item xs={12} sx={{ mt: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
                        📞 Contact Information
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">Full Name</Typography>
                      <Typography variant="body1" fontWeight="medium">{currentBooking.customizedData.yourName || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">Phone Number</Typography>
                      <Typography variant="body1">{currentBooking.customizedData.phoneNumber || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">City</Typography>
                      <Typography variant="body1">{currentBooking.customizedData.yourCity || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="text.secondary">Email</Typography>
                      <Typography variant="body1">{currentBooking.customizedData.email || 'N/A'}</Typography>
                    </Grid>
                    {currentBooking.customizedData.comments && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Comments</Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line', p: 1, bgcolor: 'white', borderRadius: 1 }}>
                          {currentBooking.customizedData.comments}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Confirm Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Booking</DialogTitle>
        <DialogContent>
          {actionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {actionError}
            </Alert>
          )}
          <Typography gutterBottom>
            Are you sure you want to confirm this booking? A confirmation email will be sent to the customer.
          </Typography>
          <TextField
            fullWidth
            label="Admin Notes (Optional)"
            placeholder="e.g., Payment received via bank transfer, confirmed by phone..."
            multiline
            rows={4}
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            sx={{ mt: 2 }}
            helperText="Add any internal notes about payment confirmation, customer communication, or special arrangements."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setConfirmDialogOpen(false);
            setAdminNotes('');
          }}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} variant="contained" color="success" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Confirm Booking'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Cancel Booking</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Cancellation Reason"
            multiline
            rows={4}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCancel} variant="contained" color="error">
            Cancel Booking
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BookingDetailsPage;
